import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { OfficialMerchBadge } from '../components/OfficialMerchBadge';
import { Button } from '../components/ui/button';
import { Crown, Globe, ShieldCheck, Truck, Sparkles } from 'lucide-react';
import { API } from '../App';

/**
 * Dedicated storefront page for the Christ Kingdom Apparel POD line.
 * URL: /store/christ-kingdom-apparel
 */
export default function HouseBrandStorefront() {
  const [info, setInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [infoRes, prodRes] = await Promise.all([
          axios.get(`${API}/products/house-brand/info`),
          axios.get(`${API}/products?house_brand=true&limit=60`),
        ]);
        setInfo(infoRes.data);
        setProducts(prodRes.data.products || []);
      } catch (e) {
        // Non-blocking — show empty state
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="house-brand-storefront">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-900 via-amber-800 to-orange-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 bg-amber-400/20 backdrop-blur-sm border border-amber-300/30 px-4 py-2 rounded-full">
              <Crown className="h-4 w-4 text-amber-200" />
              <span className="text-sm font-bold uppercase tracking-wider text-amber-100">
                Official Christ Kingdom Merchandise
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">
              {info?.name || 'Christ Kingdom Apparel'}
            </h1>
            <p className="text-2xl sm:text-3xl text-amber-100 italic font-light">
              {info?.tagline || 'Wear the Kingdom. Carry the Calling.'}
            </p>
            <p className="text-lg text-white/90 leading-relaxed max-w-2xl">
              {info?.description ||
                'Premium Christian apparel and accessories, printed-on-demand and shipped worldwide.'}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href="#products">
                <Button
                  size="lg"
                  className="rounded-full px-8 bg-white text-amber-900 hover:bg-amber-50 font-semibold shadow-xl"
                  data-testid="house-brand-shop-cta"
                >
                  Shop the Collection
                </Button>
              </a>
              <Link to="/vendor/apply">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 bg-transparent border-white text-white hover:bg-white hover:text-amber-900"
                >
                  Become a Vendor
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-amber-50/60 border-y border-amber-200/60 py-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-amber-700" />
            <span className="text-sm font-medium text-foreground">Official Platform Brand</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Globe className="h-6 w-6 text-amber-700" />
            <span className="text-sm font-medium text-foreground">Ships Worldwide</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Truck className="h-6 w-6 text-amber-700" />
            <span className="text-sm font-medium text-foreground">
              {info?.fulfilled_by ? `Fulfilled by ${info.fulfilled_by}` : 'Print-on-Demand'}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-700" />
            <span className="text-sm font-medium text-foreground">Wear-Tested Quality</span>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12" id="products">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium">The Collection</h2>
            <OfficialMerchBadge />
          </div>
          {info?.product_count !== undefined && (
            <span className="text-sm text-muted-foreground" data-testid="house-brand-count">
              {info.product_count} {info.product_count === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>

        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {['s1','s2','s3','s4','s5','s6','s7','s8'].map((k) => (
              <div key={k} className="aspect-square bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20 bg-amber-50/40 rounded-2xl border border-amber-200/60">
            <Crown className="h-16 w-16 mx-auto text-amber-300 mb-4" />
            <h3 className="text-xl font-medium text-foreground mb-2">The collection is launching soon.</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              We&apos;re finalizing our first designs. Check back in a few days, or
              <Link to="/products" className="text-primary underline ml-1">browse other Christ Kingdom vendors</Link>
              in the meantime.
            </p>
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
